import { useEffect, useMemo } from "react";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { buyerIdLayoutRoute } from "router/routes/buyers";

import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import Spinner from "components/ui/spinner/Spinner";
import { useBuyerLookup } from "lib/hooks/api/buyer/useBuyerDetailsGql";
import { BuyerDetails } from "lib/types/models";
import { stripPathPrefix } from "lib/utils";
import { mapGraphQLBuyerToLegacyFormat } from "lib/utils/buyerUtils";
import { BuyerLinkData } from "./BuyerLinkWithLogo";
import BuyerRedirectMessage from "./BuyerRedirectMessage";

/**
 * Hook for child route components to access the buyer loaded by BuyerDetailLayout.
 * Uses TanStack Query caching — the layout fetches first, so this returns cached data instantly.
 * Resolves both URL forms: GUID is canonical, but numeric ids remain valid inputs.
 */
export function useBuyerFromRoute(): BuyerDetails | null {
  const { id } = buyerIdLayoutRoute.useParams();
  const { data: buyerResults } = useBuyerLookup(id);

  return useMemo(() => {
    if (!buyerResults || buyerResults.length === 0) return null;
    return mapGraphQLBuyerToLegacyFormat(buyerResults[0]);
  }, [buyerResults]);
}

/**
 * Layout component for /buyers/$id routes.
 */
export function BuyerDetailLayout() {
  const { id } = buyerIdLayoutRoute.useParams();
  const { pathname, search } = useRouterState({ select: (s) => s.location });
  const navigate = useNavigate();

  const { data: buyerResults, isLoading } = useBuyerLookup(id);

  const buyer = useMemo((): BuyerDetails | null => {
    if (!buyerResults || buyerResults.length === 0) return null;
    return mapGraphQLBuyerToLegacyFormat(buyerResults[0]);
  }, [buyerResults]);

  // Compute redirect target (archived→single replacement, or non-canonical→GUID).
  // Reactive pathname keeps the full suffix for named child routes; the catch-all
  // _splat does not capture routes like /supplier/$supplierGuid.
  const idPrefix = `/buyers/${id}`;
  const pathSuffix = stripPathPrefix(pathname, idPrefix, "");
  // Mid-transition the pathname updates before the `id` param settles; computing a redirect
  // from the mismatched pair would drop the tab suffix. Wait until they agree again.
  const paramsSettled = pathname === idPrefix || pathname.startsWith(`${idPrefix}/`);
  const redirectTarget = useMemo(() => {
    if (!buyer || !paramsSettled) return null;

    // Archived buyer with exactly one replacement → redirect straight to the replacement's
    // GUID (single hop). The current-id guard breaks self/cyclic replacement chains.
    const replacements = buyerResults?.[0]?.replacedByBuyers;
    if (buyer.is_archived && replacements?.length === 1 && replacements[0].guid !== id) {
      return `/buyers/${replacements[0].guid}${pathSuffix}`;
    }

    // Canonicalise the URL: numeric ids and case-variant GUIDs both redirect to the exact GUID.
    if (id !== buyer.guid) {
      return `/buyers/${buyer.guid}${pathSuffix}`;
    }

    return null;
  }, [id, buyer, buyerResults, pathSuffix, paramsSettled]);

  // Redirect via useEffect — avoids the useLayoutEffect re-render loop that
  // <Navigate> causes when route params haven't settled during a pending transition.
  useEffect(() => {
    if (redirectTarget) {
      void navigate({ to: redirectTarget as string, search, replace: true });
    }
  }, [redirectTarget, navigate, search]);

  if (redirectTarget || !paramsSettled) {
    return <Spinner label="Loading buyer" />;
  }

  if (isLoading) {
    return <Spinner label="Loading buyer" />;
  }

  if (!buyer) {
    return (
      <EmptyState
        type="loadError"
        size="large"
        width="100%"
        yAxisMargin="auto"
        heading="Unable to load buyer details"
        description="We couldn't load the buyer information. This could be due to a network error or the buyer data being temporarily unavailable."
        action={
          <Button buttonType="primary" onClick={() => window.location.reload()}>
            Refresh page
          </Button>
        }
      />
    );
  }

  // Archived buyer with multiple replacements — show picker, no redirect
  if (buyer.is_archived && buyerResults?.[0]?.replacedByBuyers) {
    const replacements = buyerResults[0].replacedByBuyers;

    if (replacements.length > 1) {
      const resolvedBuyers: BuyerLinkData[] = replacements.map((r) => ({
        id: r.id,
        name: r.name,
        guid: r.guid,
        metadata: { logo_url: r.logoUrl || null, twitter_handle: null },
      }));

      return <BuyerRedirectMessage buyer={buyer} resolvedBuyers={resolvedBuyers} />;
    }
  }

  return <Outlet />;
}
